import { isNorth, normalizeBearing, northToggle, type NorthMemory, type NorthStep, type NorthView } from "./north-toggle.ts";

/**
 * Compass on a quest map. It works like the editor's North: the first press turns the
 * map north and remembers the view it left, a second press (before the map is turned
 * by hand) goes back. When the map is already north with nothing to go back to, a
 * press turns it to the route's canvas bearing, so the drawing reads upright again.
 */
export type CompassStep = NorthStep | { kind: "canvas"; target: NorthView; memory: null };

export function compassToggle(current: NorthView, memory: NorthMemory | null, canvasBearing: number): CompassStep {
  const step = northToggle(current, memory);
  if (step.kind !== "none" || isNorth(canvasBearing)) return step;
  return { kind: "canvas", target: { bearing: normalizeBearing(canvasBearing), pitch: current.pitch }, memory: null };
}

/** The view a quest map opens at (and its reset button returns to): the canvas bearing, nothing to go back to. */
export function compassStart(canvasBearing: number): { target: NorthView; memory: null } {
  return { target: { bearing: normalizeBearing(canvasBearing), pitch: 0 }, memory: null };
}

/** One +/- press: a whole zoom level, kept inside the map's limits. */
export function zoomStep(zoom: number, dir: 1 | -1, min: number, max: number): number {
  return Math.max(min, Math.min(max, zoom + dir));
}

/** Shown when the route has a turned canvas, or the map was turned away from north. */
export function compassVisible(bearing: number, canvasBearing: number): boolean {
  return !isNorth(canvasBearing) || !isNorth(bearing);
}
